import { useMemo, type CSSProperties } from "react";
import { ZoomControls } from "./ZoomControls";
import { HIGHLIGHT_BG, RESTING_HTML_BG } from "./HtmlPreviewWithHighlight";

/** A line's marker/tint. The live hover always wins over the resting marker. */
function lineTestId(strong: boolean, isResting: boolean): string | undefined {
  if (strong) return "text-line-highlighted";
  return isResting ? "text-line-resting" : undefined;
}

function lineStyle(
  strong: boolean,
  isResting: boolean,
): CSSProperties | undefined {
  if (strong) return { background: HIGHLIGHT_BG };
  return isResting ? { background: RESTING_HTML_BG } : undefined;
}

/**
 * Plain-text preview: renders each raw line as its own row so the flow's cited
 * line numbers (`highlightedTextLines`, 1-based into the raw `\n`-split text)
 * can be highlighted — the text analog of the html/excel row highlight
 * (STK-1360).
 */
export function TextPreviewWithHighlight({
  textContent,
  highlightedTextLines,
  restingTextLines,
  onLineHover,
  scale,
  onScaleChange,
  onClose,
  onDownload,
  isDownloading,
}: Readonly<{
  textContent: string;
  highlightedTextLines: number[];
  /** STK-2338: lines to tint faintly because their flow is hovered from the
   * recap side (doc → flow is the STRONG direction; this is the resting one). */
  restingTextLines?: number[];
  /** STK-2338: fired as the pointer enters/leaves a line, with its 1-based
   * ordinal, or `null` on leave. */
  onLineHover?: (line: number | null) => void;
  scale: number;
  onScaleChange: (s: number) => void;
  onClose: () => void;
  onDownload: () => void;
  isDownloading: boolean;
}>) {
  const hi = useMemo(
    () => new Set(highlightedTextLines),
    [highlightedTextLines],
  );
  const resting = useMemo(
    () => new Set(restingTextLines ?? []),
    [restingTextLines],
  );
  // Carries each line's 1-based ordinal — the address the flow's cited line
  // numbers use — so it is both the React key and the hover payload.
  const lines = useMemo(
    () => textContent.split("\n").map((text, i) => ({ text, ordinal: i + 1 })),
    [textContent],
  );
  return (
    <div className="flex flex-col h-full">
      <ZoomControls
        scale={scale}
        onScaleChange={onScaleChange}
        onReset={() => onScaleChange(1)}
        onClose={onClose}
        onDownload={onDownload}
        isDownloading={isDownloading}
      />
      {/* overflow-auto + whitespace-pre (no wrap) gives both vertical AND
          horizontal scrollbars, so long/zoomed rows stay on one line and the
          user can scroll sideways instead of the text wrapping. w-max sizes
          the <pre> to its longest line so the horizontal scroll range (and
          right padding) is correct. */}
      <div className="flex-1 overflow-auto bg-white">
        <pre
          className="p-4 font-mono text-foreground whitespace-pre w-max min-w-full"
          style={{ fontSize: `${scale}rem` }}
        >
          {lines.map(({ text, ordinal }) => {
            const strong = hi.has(ordinal);
            const isResting = !strong && resting.has(ordinal);
            return (
              <div
                key={ordinal}
                data-testid={lineTestId(strong, isResting)}
                onMouseEnter={() => onLineHover?.(ordinal)}
                onMouseLeave={() => onLineHover?.(null)}
                style={lineStyle(strong, isResting)}
              >
                {text || " "}
              </div>
            );
          })}
        </pre>
      </div>
    </div>
  );
}
